import React, { useEffect, useState } from 'react';
import { useStore } from '@/contexts/StoreContext';
import {
  launcherService,
  formatBytes,
  type DownloadEntry,
  type LauncherDownloads,
} from '@/lib/services/launcher.service';
import { Download, Monitor, Apple, Terminal, ShieldAlert, Loader2, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHero from '@/components/common/PageHero';
import { Caixa } from '@/construtor/caixa';

const TARGETS: Record<
  string,
  { label: string; icon: React.ElementType; platform: 'windows' | 'mac' | 'linux' | 'android' }
> = {
  'win-x64': { label: 'Windows', icon: Monitor, platform: 'windows' },
  'linux-x64': { label: 'Linux', icon: Terminal, platform: 'linux' },
  'mac-x64': { label: 'macOS (Intel)', icon: Apple, platform: 'mac' },
  'mac-arm64': { label: 'macOS (Apple Silicon)', icon: Apple, platform: 'mac' },
  'android-arm64': { label: 'Android', icon: Smartphone, platform: 'android' },
};

/**
 * Detecta a plataforma para destacar o download certo.
 *
 * É só um destaque — todas as opções continuam visíveis. Esconder as outras
 * quebraria o caso real de quem baixa no trabalho para instalar em casa, e a
 * detecção erra em Apple Silicon com navegador em modo Rosetta.
 */
function detectPlatform(): 'windows' | 'mac' | 'linux' | 'android' | null {
  if (typeof navigator === 'undefined') return null;

  const ua = navigator.userAgent.toLowerCase();

  // Antes de `linux`: o user agent do Android também diz Linux.
  if (ua.includes('android')) return 'android';
  if (ua.includes('win')) return 'windows';
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('linux') || ua.includes('x11')) return 'linux';

  return null;
}

export default function DownloadPage() {
  const { t } = useTranslation();
  const { store } = useStore();
  const [data, setData] = useState<LauncherDownloads | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const platform = detectPlatform();

  useEffect(() => {
    if (!store?.id) return;

    launcherService
      .getDownloads(store.id)
      .then(setData)
      .catch((err: any) => {
        // 403 = loja suspensa, cancelada ou sem o recurso no plano. A mensagem
        // vem traduzida da API, a mesma que o launcher instalado mostraria —
        // para o jogador não receber duas explicações do mesmo fato.
        setUnavailable(
          err?.response?.data?.message ??
            t('store.download.unavailableDefault')
        );
      })
      .finally(() => setLoading(false));
  }, [store?.id]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <h1 className="mb-3 text-2xl font-bold">{t('store.download.unavailableTitle')}</h1>
        <p className="text-muted-foreground">{unavailable}</p>
      </div>
    );
  }

  const hasDownloads = (data?.downloads.length ?? 0) > 0;
  const recommended = data?.downloads.find(
    (entry) => TARGETS[entry.target]?.platform === platform
  );
  const others = data?.downloads.filter((entry) => entry !== recommended) ?? [];

  return (
    <div>
      <PageHero
        title={t('store.download.title')}
        breadcrumbs={[{ label: t('store.download.breadcrumb') }]}
      />

      <div className="mx-auto max-w-3xl px-4 py-12">
        <Caixa id="download.cabecalho">
        <div className="mb-10 text-center">
          <h2 className="mb-2 text-2xl font-bold">
            {t('store.download.heading', {
              app: data?.displayName || data?.appName || 'launcher',
            })}
          </h2>
          <p className="text-muted-foreground">{t('store.download.subtitle')}</p>
          {data?.coreVersion && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('store.download.version', { version: data.coreVersion })}
              {data.publishedAt &&
                ` · ${t('store.download.publishedAt', {
                  date: new Date(data.publishedAt).toLocaleDateString(),
                })}`}
            </p>
          )}
        </div>
        </Caixa>

      {!hasDownloads ? (
        <Caixa id="download.vazio">
        <div className="rounded-xl border border-border p-10 text-center">
          <Download className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-medium">{t('store.download.emptyTitle')}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('store.download.emptyBody')}
          </p>
        </div>
        </Caixa>
      ) : (
        <Caixa id="download.lista">
        <div className="space-y-4">
          {recommended && (
            <Caixa id="download.recomendado" envolver>
            <DownloadCard entry={recommended} highlighted />
            </Caixa>
          )}

          {others.length > 0 && (
            <div className="space-y-2">
              {recommended && (
                <p className="pt-4 text-sm font-medium text-muted-foreground">
                  {t('store.download.otherPlatforms')}
                </p>
              )}
              {others.map((entry) => (
                <Caixa id="download.opcao" repetida envolver key={entry.target}>
                <DownloadCard key={entry.target} entry={entry} />
                </Caixa>
              ))}
            </div>
          )}
        </div>
        </Caixa>
      )}

      {/*
        O aviso do sistema operacional.
        Na v1 os instaladores não são assinados (ADR-05), então o Windows mostra
        "app não reconhecido" e o macOS recusa o primeiro duplo-clique. Sem esta
        explicação o jogador conclui que o arquivo tem vírus e desiste — e a
        loja perde a instalação sem nunca saber por quê.
      */}
      {hasDownloads && (
        <Caixa id="download.aviso">
        <div className="mt-10 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="mb-3 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            <h2 className="font-semibold">{t('store.download.warnTitle')}</h2>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('store.download.warnBody')}
          </p>

          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium">{t('store.download.warnWindows')}</p>
              <p className="text-muted-foreground">{t('store.download.warnWindowsBody')}</p>
            </div>
            <div>
              <p className="font-medium">{t('store.download.warnMac')}</p>
              <p className="text-muted-foreground">{t('store.download.warnMacBody')}</p>
            </div>
            {data?.downloads.some((entry) => entry.target === 'android-arm64') && (
              <div>
                <p className="font-medium">{t('store.download.warnAndroid')}</p>
                <p className="text-muted-foreground">{t('store.download.warnAndroidBody')}</p>
              </div>
            )}
            <div>
              <p className="font-medium">{t('store.download.warnCheck')}</p>
              <p className="text-muted-foreground">{t('store.download.warnCheckBody')}</p>
            </div>
          </div>
        </div>
        </Caixa>
      )}
      </div>
    </div>
  );
}

function DownloadCard({
  entry,
  highlighted,
}: {
  entry: DownloadEntry;
  highlighted?: boolean;
}) {
  const { t } = useTranslation();
  const target = TARGETS[entry.target];
  const Icon = target?.icon ?? Download;

  return (
    <div
      className={`rounded-xl border p-5 ${
        highlighted ? 'border-primary bg-primary/5' : 'border-border'
      }`}
    >
      <div className="flex items-center gap-4">
        <Icon className="h-8 w-8 shrink-0 text-primary" />

        <div className="min-w-0 flex-1">
          <p className="font-semibold">{target?.label ?? entry.target}</p>
          <p className="text-xs text-muted-foreground">
            {formatBytes(entry.sizeBytes)}
            {highlighted && ` · ${t('store.download.yourSystem')}`}
          </p>
        </div>

        <a
          href={entry.url}
          className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            highlighted
              ? 'bg-primary text-primary-foreground hover:opacity-90'
              : 'border border-border hover:bg-muted'
          }`}
          // O arquivo vem do CDN, outra origem: sem `noopener` a página de
          // destino ganharia referência ao nosso `window`.
          rel="noopener noreferrer"
        >
          <Download className="h-4 w-4" />
          {t('store.download.download')}
        </a>
      </div>

      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-muted-foreground">
          {t('store.download.checksum')}
        </summary>
        <p className="mt-2 break-all font-mono text-[11px] text-muted-foreground">
          {entry.sha256}
        </p>
      </details>
    </div>
  );
}
